import type { BuilderAssistant, BuilderCatalog, BuilderHost, BuilderRecipe, PromptObservationReach } from '../../lib/api'
import { paramFrom, withParams } from '../../lib/tabs'

/**
 * The policy builder's state and the questions it asks of the catalog, kept out of the components so
 * they can be tested without rendering anything.
 *
 * Every choice lives in the URL (D-441). An operator who sends a colleague a link to "Claude, in a
 * browser, block sensitive prompts" should be sending them exactly that, and the back button should
 * step back through the wizard rather than leave it. The one thing that does not is the name, which is
 * a text field somebody is typing into rather than a choice a link should carry.
 *
 * Whether a recipe fits a host is never decided here. The server sends the answer, from the same
 * function that refuses a group it cannot build, so this file only reads it.
 */

export type BuilderStep = 'what' | 'host' | 'recipe' | 'message'
export type BuilderTarget = 'assistant' | 'computer'
export type HostKind = BuilderHost['host']

export interface BuilderState {
  step: BuilderStep
  target: BuilderTarget | null
  assistant: string | null
  hosts: HostKind[]
  recipe: string | null
  screenshot: boolean
  dictionaries: string[]
  /** The violation message the notifying policies show, or null for the recipe's own words (ADR-S-053). */
  message: string | null
}

const STEPS: readonly BuilderStep[] = ['what', 'host', 'recipe', 'message']
const HOSTS: readonly HostKind[] = ['browser', 'desktop', 'terminal']

function list(value: string | null): string[] {
  return value === null ? [] : value.split(',').map((item) => item.trim()).filter((item) => item.length > 0)
}

/** The state a query string describes, before it has been checked against the catalog. */
export function parseBuilder(search: string): BuilderState {
  const step = paramFrom(search, 'step')
  const target = paramFrom(search, 'target')
  return {
    step: STEPS.includes(step as BuilderStep) ? (step as BuilderStep) : 'what',
    target: target === 'assistant' || target === 'computer' ? target : null,
    assistant: paramFrom(search, 'assistant'),
    hosts: [...new Set(list(paramFrom(search, 'hosts')).filter((host): host is HostKind => HOSTS.includes(host as HostKind)))],
    recipe: paramFrom(search, 'recipe'),
    screenshot: paramFrom(search, 'shot') === '1',
    dictionaries: [...new Set(list(paramFrom(search, 'dict')))],
    message: paramFrom(search, 'msg')
  }
}

/** The query string for a state, keeping anything else that was already there. */
export function serializeBuilder(search: string, state: BuilderState): string {
  return withParams(search, {
    step: state.step === 'what' ? null : state.step,
    target: state.target,
    assistant: state.target === 'assistant' ? state.assistant : null,
    hosts: state.target === 'assistant' && state.hosts.length > 0 ? state.hosts.join(',') : null,
    recipe: state.recipe,
    shot: state.screenshot ? '1' : null,
    dict: state.dictionaries.length > 0 ? state.dictionaries.join(',') : null,
    msg: state.message
  })
}

export function findAssistant(catalog: BuilderCatalog, key: string | null): BuilderAssistant | undefined {
  return key === null ? undefined : catalog.assistants.find((assistant) => assistant.key === key)
}

export function hostsFor(catalog: BuilderCatalog, assistantKey: string | null): BuilderHost[] {
  return findAssistant(catalog, assistantKey)?.hosts ?? []
}

/** The catalog keys the chosen hosts stand for, in the order the assistant lists them. */
export function agentKeysFor(catalog: BuilderCatalog, assistantKey: string | null, hosts: readonly HostKind[]): string[] {
  return hostsFor(catalog, assistantKey)
    .filter((host) => hosts.includes(host.host))
    .map((host) => host.agentKey)
}

export function recipesFor(catalog: BuilderCatalog, target: BuilderTarget | null): BuilderRecipe[] {
  return target === null ? [] : catalog.recipes.filter((recipe) => recipe.target === target)
}

export interface RecipeAvailability {
  enabled: boolean
  /** Why each refusing host refuses, in the server's words. */
  reasons: { agentKey: string; displayName: string; reason: string }[]
  /** Things true of a supported choice that the operator should still read. */
  notes: string[]
}

/**
 * Whether a recipe can be built for every chosen host.
 *
 * Greyed out if any one host cannot, rather than offered "where it can": the builder promises one
 * decision covering every host somebody ticked, and quietly covering fewer would be a promise the list
 * page then contradicts. The reason names the host so the operator can untick it.
 */
export function recipeAvailability(catalog: BuilderCatalog, recipe: BuilderRecipe, agentKeys: readonly string[]): RecipeAvailability {
  if (recipe.target === 'computer') return { enabled: true, reasons: [], notes: [] }

  const names = new Map(catalog.assistants.flatMap((assistant) => assistant.hosts.map((host) => [host.agentKey, host.displayName] as const)))
  const reasons: RecipeAvailability['reasons'] = []
  const notes = new Set<string>()

  for (const agentKey of agentKeys) {
    const cell = catalog.support.find((entry) => entry.recipeKey === recipe.key && entry.agentKey === agentKey)
    if (!cell || !cell.supported)
      reasons.push({
        agentKey,
        displayName: names.get(agentKey) ?? agentKey,
        reason: cell?.reason ?? 'The server does not offer this here.'
      })
    else if (cell.note) notes.add(cell.note)
  }

  return { enabled: agentKeys.length > 0 && reasons.length === 0, reasons, notes: [...notes] }
}

/**
 * The state with every choice the catalog no longer allows removed, and the step moved back to the
 * first one that still needs an answer.
 *
 * Run on every render rather than only on change, because a pasted or edited URL is a state nobody
 * clicked their way into. Changing the assistant drops hosts it does not have; an assistant with one
 * host has it chosen for them; a recipe the hosts cannot all support is dropped rather than kept
 * disabled, so the review never describes something the server will refuse.
 */
export function normalizeBuilder(state: BuilderState, catalog: BuilderCatalog): BuilderState {
  const next: BuilderState = { ...state, hosts: [...state.hosts], dictionaries: [...state.dictionaries] }

  if (next.target !== 'assistant') {
    next.assistant = null
    next.hosts = []
    next.screenshot = false
  } else {
    const assistant = findAssistant(catalog, next.assistant)
    if (!assistant) {
      next.assistant = null
      next.hosts = []
    } else {
      const available = assistant.hosts.map((host) => host.host)
      next.hosts = available.length === 1 ? available : next.hosts.filter((host) => available.includes(host))
    }
  }

  const recipe = catalog.recipes.find((candidate) => candidate.key === next.recipe)
  if (
    !recipe ||
    recipe.target !== next.target ||
    !recipeAvailability(catalog, recipe, agentKeysFor(catalog, next.assistant, next.hosts)).enabled
  )
    next.recipe = null

  if (!recipe || !recipe.needsDictionary || next.recipe === null) next.dictionaries = []
  if (!recipe || !recipe.notifies || next.recipe === null) next.message = null
  if (next.recipe === null || next.target !== 'assistant') next.screenshot = false

  // The furthest step the answers so far allow. The message step exists only once a recipe that tells
  // the person is chosen, because for any other there is nothing to say.
  const answered: BuilderStep =
    next.target === null
      ? 'what'
      : next.target === 'computer'
        ? 'recipe'
        : next.assistant === null
          ? 'what'
          : next.hosts.length === 0
            ? 'host'
            : 'recipe'
  const furthest: BuilderStep = answered === 'recipe' && next.recipe !== null && recipe?.notifies ? 'message' : answered

  if (STEPS.indexOf(next.step) > STEPS.indexOf(furthest)) next.step = furthest
  // The host step is never shown for an assistant with only one host, because there is no question.
  if (next.step === 'host' && hostsFor(catalog, next.assistant).length === 1) next.step = 'recipe'
  return next
}

/** Whether the host step is a question for this assistant at all. */
export function asksForHost(catalog: BuilderCatalog, state: BuilderState): boolean {
  return state.target === 'assistant' && hostsFor(catalog, state.assistant).length > 1
}

/** Whether the chosen recipe tells the person, which is what gives the builder its fourth step. */
export function asksForMessage(catalog: BuilderCatalog, state: BuilderState): boolean {
  return catalog.recipes.find((candidate) => candidate.key === state.recipe)?.notifies === true
}

export function previousStep(catalog: BuilderCatalog, state: BuilderState): BuilderStep | null {
  if (state.step === 'message') return 'recipe'
  if (state.step === 'recipe') return asksForHost(catalog, state) ? 'host' : 'what'
  if (state.step === 'host') return 'what'
  return null
}

/** The name the group gets unless the operator types one, matching what the server would choose. */
export function defaultGroupName(catalog: BuilderCatalog, state: BuilderState): string {
  const recipe = catalog.recipes.find((candidate) => candidate.key === state.recipe)
  if (!recipe) return ''
  const assistant = findAssistant(catalog, state.assistant)
  return recipe.target === 'assistant' && assistant ? `${recipe.label} in ${assistant.displayName}` : recipe.label
}

function joined(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

const HOST_PHRASE: Record<HostKind, string> = {
  browser: 'in a browser',
  desktop: 'in the desktop app',
  terminal: 'in a terminal'
}

/**
 * What the choices add up to, as one sentence an operator can check before anything is created. Says
 * how many policies that is, because "one choice, three policies" is the thing the builder hides and
 * the list page will show.
 */
export function summarySentence(catalog: BuilderCatalog, state: BuilderState): string | null {
  const recipe = catalog.recipes.find((candidate) => candidate.key === state.recipe)
  if (!recipe) return null

  if (recipe.target === 'computer') return `${recipe.label}, on every computer this policy is published to. That is one policy.`

  const assistant = findAssistant(catalog, state.assistant)
  if (!assistant || state.hosts.length === 0) return null

  const hosts = assistant.hosts.filter((host) => state.hosts.includes(host.host)).map((host) => HOST_PHRASE[host.host])
  const count = state.hosts.length * (state.screenshot ? 2 : 1)
  const screenshot = state.screenshot ? ' Take a screenshot when one is flagged.' : ''
  return `${recipe.label}: ${assistant.displayName} ${joined(hosts)}.${screenshot} That is ${count} ${count === 1 ? 'policy' : 'policies'}, approved and published together.`
}

/**
 * How many of this organization's devices can watch an assistant, in a sentence. Every number is the
 * server's count over the whole fleet. "Did not say" is kept apart from "cannot", because an agent
 * before 1.2.0 reports nothing and may well be one somebody is about to upgrade.
 */
export function reachSentence(reach: PromptObservationReach | null | undefined): string | null {
  if (!reach || reach.active === 0) return null
  const devices = (count: number) => `${count} ${count === 1 ? 'device' : 'devices'}`
  const parts = [`${reach.capable} of ${devices(reach.active)} report they can watch an assistant`]
  if (reach.incapable > 0) parts.push(`${devices(reach.incapable)} ${reach.incapable === 1 ? 'reports' : 'report'} it cannot`)
  if (reach.unknown > 0) parts.push(`${devices(reach.unknown)} ${reach.unknown === 1 ? 'runs' : 'run'} an agent too old to say`)
  return `${parts.join('; ')}.`
}
